/**
 * Runs a spec: checks it against what the input and ports offer, then runs
 * each step in order, applying its output and tracing what it did.
 */

import { normalizeExtraction } from '../extraction.js';
import { validateExtraction } from '../validate.js';
import { currentReading } from './steps/support.js';
import type { Key, Ports, ReadingInput, ReadingState, Spec, SpecEntry, Step, StepOutput, StepTrace } from './types.js';

export type StepRegistry = ReadonlyMap<string, Step<never>>;

export function stepRegistry(...steps: readonly Step<never>[]): Map<string, Step<never>> {
  return new Map(steps.map((step) => [step.name, step]));
}

/** A spec whose steps cannot all run: a step missing, or one needing what nothing before it provides. */
export class SpecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SpecError';
  }
}

/** Conditions a spec entry can be `when`-guarded by. */
export const CONDITIONS: Record<string, (state: Readonly<ReadingState>) => boolean> = {
  /** The reading so far does not hold together (`validateExtraction`). */
  invalid: (state) => !validateExtraction(normalizeExtraction(currentReading(state))).ok,
  /** A model step left the category list out to fit its budget. */
  'categories-dropped': (state) => state.facts.categoriesDropped === true,
};

/** What the input and ports offer before any step runs. */
export function initialKeys(input: ReadingInput, ports: Ports): Set<Key> {
  const keys = new Set<Key>();
  if (input.text?.trim()) keys.add('text');
  if (input.lines?.length) keys.add('lines');
  if (input.image) keys.add('image');
  if (input.categories.length > 0) keys.add('categories');
  if (ports.transport) keys.add('transport');
  if (ports.companyLookup) keys.add('company-lookup');
  return keys;
}

const idOf = (entry: SpecEntry): string => entry.id ?? entry.step;

/**
 * Checks every step exists, has a distinct id and a known condition, and gets
 * what it needs from the input, the ports or a step before it. An optional
 * step may go without: it is skipped at run time. Returns the ids of the
 * optional steps that will be skipped.
 */
export function checkSpec(spec: Spec, registry: StepRegistry, available: ReadonlySet<Key>): Set<string> {
  const keys = new Set(available);
  const ids = new Set<string>();
  const skipped = new Set<string>();
  for (const entry of spec) {
    const step = registry.get(entry.step) as Step<unknown> | undefined;
    const id = idOf(entry);
    if (!step) throw new SpecError(`unknown step "${entry.step}"`);
    if (ids.has(id)) throw new SpecError(`two steps with the id "${id}"`);
    ids.add(id);
    if (entry.when && !CONDITIONS[entry.when]) throw new SpecError(`${id}: unknown condition "${entry.when}"`);
    const missing = step.needs(entry.options).filter((key) => !keys.has(key));
    if (missing.length > 0) {
      if (!entry.optional) throw new SpecError(`${id} needs ${missing.join(', ')}, which nothing before it provides`);
      skipped.add(id);
      continue;
    }
    for (const key of step.provides(entry.options, id)) keys.add(key);
  }
  return skipped;
}

function apply(state: ReadingState, id: string, output: StepOutput): void {
  for (const [field, list] of Object.entries(output.candidates ?? {})) {
    const key = field as keyof ReadingState['candidates'];
    state.candidates[key] = [...(state.candidates[key] ?? []), ...(list ?? [])];
  }
  if (output.resolved) state.resolved = output.resolved;
  if (output.company !== undefined) state.company = output.company;
  if (output.lines) state.input = { ...state.input, lines: output.lines };
  if (output.answer) state.answers[id] = output.answer;
  if (output.facts) state.facts = { ...state.facts, ...output.facts };
}

export function emptyState(input: ReadingInput): ReadingState {
  return { input, candidates: {}, resolved: {}, company: null, answers: {}, facts: {}, trace: [] };
}

/**
 * Runs `spec` over `input`. A required step that throws fails the reading
 * (the caller's job retries it); an optional one is traced and skipped.
 */
export async function runReading(spec: Spec, registry: StepRegistry, input: ReadingInput, ports: Ports): Promise<ReadingState> {
  const unmet = checkSpec(spec, registry, initialKeys(input, ports));
  const state = emptyState(input);
  const around = ports.around ?? (<T>(_id: string, work: () => Promise<T>) => work());

  for (const entry of spec) {
    const id = idOf(entry);
    const step = registry.get(entry.step) as Step<unknown>;
    const trace: StepTrace = { id, step: entry.step, status: 'ok', ms: 0, added: [] };
    state.trace.push(trace);
    if (unmet.has(id)) {
      trace.status = 'skipped';
      trace.reason = `needs ${step.needs(entry.options).join(', ')}`;
      continue;
    }
    if (entry.when && !CONDITIONS[entry.when]!(state)) {
      trace.status = 'skipped';
      trace.reason = `not ${entry.when}`;
      continue;
    }
    const started = Date.now();
    try {
      const output = await around(id, () => step.run(state, { id, options: entry.options, ports }));
      trace.ms = Date.now() - started;
      if (output.skipped) {
        trace.status = 'skipped';
        trace.reason = output.skipped;
      }
      trace.added = output.added ?? [];
      if (output.dropped?.length) trace.dropped = output.dropped;
      if (output.tokens) trace.tokens = output.tokens;
      if (output.chosen) trace.chosen = output.chosen;
      apply(state, id, output);
    } catch (error) {
      trace.ms = Date.now() - started;
      trace.status = 'failed';
      trace.reason = error instanceof Error ? error.message : String(error);
      if (!entry.optional) throw error;
    }
  }
  return state;
}
